/**
 * CascadePlanner - Interface for planning cascade re-execution from feedback points
 * Integrates with HISI-003 cascade system for intelligent dependency analysis
 */

import React, { useState, useEffect } from 'react';
import { joinApiPath } from '@/lib/api-config';
import { 
  ArrowRight,
  Clock,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Zap,
  BarChart3,
  Play,
  RefreshCw,
  Target,
  Layers
} from 'lucide-react';
import { StageType, ExecutionStatus } from '@/types/execution';
import { UserFeedbackEvaluation } from './FeedbackPanel';
import { cn } from '@/utils/cn';

interface CascadePlan {
  trigger_stage: StageType;
  affected_stages: StageImpact[];
  preserved_refinements: string[];
  lost_refinements: string[];
  execution_strategy: 'sequential' | 'parallel' | 'hybrid';
  estimated_duration_seconds: number;
  estimated_cost_usd: number;
  confidence_score: number;
  risk_assessment: {
    complexity: 'low' | 'medium' | 'high';
    cost_risk: 'low' | 'medium' | 'high';
    time_risk: 'low' | 'medium' | 'high';
  };
}

interface StageImpact {
  stage_id: StageType;
  impact_type: 'full_regen' | 'context_update' | 'style_adjust' | 'validation_only';
  reasoning: string;
  depends_on: string[];
  estimated_time: number;
  preserves_existing_refinements: boolean;
}

interface CascadePlannerProps {
  execution: ExecutionStatus;
  feedback: UserFeedbackEvaluation;
  onExecuteCascade: (plan: CascadePlan, scope: CascadeScope, targetStage?: StageType) => void;
  onCancel: () => void;
  className?: string;
}

type CascadeScope = 'single_stage' | 'to_stage' | 'to_end';

const STAGE_LABELS = {
  script: 'Script',
  vision: 'Vision', 
  storyboard: 'Storyboard',
  shots: 'Shot List',
  continuity: 'Continuity',
  frames: 'Frames',
  videos: 'Videos',
  elements: 'Elements',
  qc: 'QC',
  assemble: 'Assemble',
  music: 'Music',
  publish: 'Publish',
  music_master: 'Music Master',
  music_sidechain: 'Music Sidechain',
  export_preview: 'Export Preview'
} as const;

const STAGE_ORDER: StageType[] = ['script', 'vision', 'storyboard', 'shots', 'continuity', 'frames', 'videos'];

const CascadePlanner: React.FC<CascadePlannerProps> = ({
  execution,
  feedback,
  onExecuteCascade,
  onCancel,
  className
}) => {
  const [cascadeScope, setCascadeScope] = useState<CascadeScope>('to_end');
  const [targetStage, setTargetStage] = useState<StageType>('videos');
  const [plan, setPlan] = useState<CascadePlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get available target stages (stages after the trigger stage)
  const triggerIndex = STAGE_ORDER.indexOf(feedback.stage);
  const availableTargetStages = STAGE_ORDER.slice(triggerIndex + 1);

  // Load cascade plan when scope changes
  useEffect(() => {
    loadCascadePlan();
  }, [cascadeScope, targetStage, feedback.stage]);

  const loadCascadePlan = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(joinApiPath('cascade', 'plan'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          execution_id: execution.id,
          trigger_stage: feedback.stage,
          cascade_scope: cascadeScope,
          target_stage: cascadeScope === 'to_stage' ? targetStage : undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate cascade plan');
      }

      const data = await response.json();
      if (data.success) {
        setPlan(data.plan);
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cascade plan');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = () => {
    if (plan) {
      onExecuteCascade(plan, cascadeScope, cascadeScope === 'to_stage' ? targetStage : undefined);
    }
  };

  const getImpactTypeIcon = (impactType: string) => {
    switch (impactType) {
      case 'full_regen':
        return <RefreshCw className="w-4 h-4 text-orange-600" />;
      case 'context_update':
        return <ArrowRight className="w-4 h-4 text-blue-600" />;
      case 'style_adjust':
        return <Zap className="w-4 h-4 text-purple-600" />;
      default:
        return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
  };

  const getImpactTypeLabel = (impactType: string) => {
    switch (impactType) {
      case 'full_regen':
        return 'Full Regeneration';
      case 'context_update':
        return 'Context Update';
      case 'style_adjust':
        return 'Style Adjustment';
      case 'validation_only':
        return 'Validation Only';
      default:
        return 'Unknown';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  return (
    <div className={cn('bg-black/95 backdrop-blur-xl rounded-2xl border border-purple-500/20 shadow-2xl', className)}>
      {/* Header - Already styled by ExecutionPortal wrapper */}
      <div className="hidden"></div>

      {/* Scope Selection */}
      <div className="p-6 bg-gradient-to-r from-purple-500/5 to-emerald-500/5 border-b border-white/10">
        <div className="mb-4">
          <h4 className="text-sm font-mono text-white/50 uppercase tracking-wider mb-2">Cascade Scope</h4>
          <p className="text-xs text-white/40">
            Choose how far the cascade should propagate from the <span className="text-purple-400 font-mono">{STAGE_LABELS[feedback.stage]}</span> stage
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <button
            onClick={() => setCascadeScope('single_stage')}
            className={cn(
              'p-4 rounded-lg border text-left transition-all duration-300 group',
              cascadeScope === 'single_stage'
                ? 'border-purple-500/50 bg-purple-500/10 shadow-lg shadow-purple-500/20'
                : 'border-white/10 bg-black/30 hover:border-purple-500/30 hover:bg-purple-500/5'
            )}
          >
            <Target className={cn(
              "w-5 h-5 mb-2",
              cascadeScope === 'single_stage' ? 'text-purple-400' : 'text-white/40 group-hover:text-purple-400/60'
            )} />
            <div className={cn(
              "text-sm font-medium",
              cascadeScope === 'single_stage' ? 'text-purple-400' : 'text-white/70'
            )}>Single Stage</div>
            <div className="text-xs text-white/40">Only refine trigger stage</div>
          </button>

          <button
            onClick={() => setCascadeScope('to_stage')}
            disabled={availableTargetStages.length === 0}
            className={cn(
              'p-4 rounded-lg border text-left transition-all duration-300 group',
              cascadeScope === 'to_stage'
                ? 'border-emerald-500/50 bg-emerald-500/10 shadow-lg shadow-emerald-500/20'
                : 'border-white/10 bg-black/30 hover:border-emerald-500/30 hover:bg-emerald-500/5',
              availableTargetStages.length === 0 && 'opacity-30 cursor-not-allowed'
            )}
          >
            <ArrowRight className={cn(
              "w-5 h-5 mb-2",
              cascadeScope === 'to_stage' ? 'text-emerald-400' : 'text-white/40 group-hover:text-emerald-400/60'
            )} />
            <div className={cn(
              "text-sm font-medium",
              cascadeScope === 'to_stage' ? 'text-emerald-400' : 'text-white/70'
            )}>To Specific Stage</div>
            <div className="text-xs text-white/40">Cascade to chosen stage</div>
          </button>

          <button
            onClick={() => setCascadeScope('to_end')}
            disabled={availableTargetStages.length === 0}
            className={cn(
              'p-4 rounded-lg border text-left transition-all duration-300 group',
              cascadeScope === 'to_end'
                ? 'border-cyan-500/50 bg-cyan-500/10 shadow-lg shadow-cyan-500/20'
                : 'border-white/10 bg-black/30 hover:border-cyan-500/30 hover:bg-cyan-500/5',
              availableTargetStages.length === 0 && 'opacity-30 cursor-not-allowed'
            )}
          >
            <Layers className={cn(
              "w-5 h-5 mb-2",
              cascadeScope === 'to_end' ? 'text-cyan-400' : 'text-white/40 group-hover:text-cyan-400/60'
            )} />
            <div className={cn(
              "text-sm font-medium",
              cascadeScope === 'to_end' ? 'text-cyan-400' : 'text-white/70'
            )}>To End</div>
            <div className="text-xs text-white/40">Complete pipeline cascade</div>
          </button>
        </div>

        {/* Target Stage Selector */}
        {cascadeScope === 'to_stage' && availableTargetStages.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Stage
            </label>
            <select
              value={targetStage}
              onChange={(e) => setTargetStage(e.target.value as StageType)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {availableTargetStages.map((stage) => (
                <option key={stage} value={stage}>
                  {STAGE_LABELS[stage]}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Plan Results */}
      {loading && (
        <div className="p-6 text-center">
          <RefreshCw className="w-6 h-6 mx-auto mb-2 text-blue-600 animate-spin" />
          <p className="text-sm text-gray-600">Analyzing cascade impact...</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <div className="flex items-center space-x-2">
            <XCircle className="w-4 h-4 text-red-600" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
        </div>
      )}

      {plan && !loading && (
        <>
          {/* Overview */}
          <div className="p-4 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Layers className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Stages</span>
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {plan.affected_stages.length}
                </div>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Time</span>
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {formatDuration(plan.estimated_duration_seconds)}
                </div>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Cost</span>
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  ${plan.estimated_cost_usd.toFixed(2)}
                </div>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <BarChart3 className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Confidence</span>
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {Math.round(plan.confidence_score * 100)}%
                </div>
              </div>
            </div>

            {/* Risk Assessment */}
            <div className="flex items-center justify-center space-x-4">
              <div className={cn('px-2 py-1 rounded text-xs font-medium', getRiskColor(plan.risk_assessment.complexity))}>
                {plan.risk_assessment.complexity.toUpperCase()} COMPLEXITY
              </div>
              <div className={cn('px-2 py-1 rounded text-xs font-medium', getRiskColor(plan.risk_assessment.cost_risk))}>
                {plan.risk_assessment.cost_risk.toUpperCase()} COST
              </div>
              <div className={cn('px-2 py-1 rounded text-xs font-medium', getRiskColor(plan.risk_assessment.time_risk))}>
                {plan.risk_assessment.time_risk.toUpperCase()} TIME
              </div>
            </div>
          </div>

          {/* Stage Impacts */}
          <div className="p-4 border-b border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Stage Impact Analysis</h4>
            <div className="space-y-3">
              {plan.affected_stages.map((impact, index) => (
                <div
                  key={impact.stage_id}
                  className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-shrink-0">
                    {getImpactTypeIcon(impact.impact_type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-gray-900">
                        {STAGE_LABELS[impact.stage_id]}
                      </span>
                      <span className={cn(
                        'px-2 py-0.5 rounded text-xs font-medium',
                        impact.preserves_existing_refinements
                          ? 'bg-green-100 text-green-800'
                          : 'bg-orange-100 text-orange-800'
                      )}>
                        {impact.preserves_existing_refinements ? 'Preserves Refinements' : 'Loses Refinements'}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>{getImpactTypeLabel(impact.impact_type)}</span>
                      <span>•</span>
                      <span>{formatDuration(impact.estimated_time)}</span>
                    </div>
                    
                    {impact.reasoning && (
                      <p className="text-xs text-gray-500 mt-1">{impact.reasoning}</p>
                    )}
                  </div>
                  
                  {index < plan.affected_stages.length - 1 && (
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Refinement Impact */}
          {(plan.preserved_refinements.length > 0 || plan.lost_refinements.length > 0) && (
            <div className="p-4 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Refinement Impact</h4>
              
              {plan.preserved_refinements.length > 0 && (
                <div className="mb-2">
                  <div className="flex items-center space-x-1 mb-1">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    <span className="text-xs font-medium text-green-800">Preserved</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {plan.preserved_refinements.map(stage => STAGE_LABELS[stage as StageType]).join(', ')}
                  </div>
                </div>
              )}
              
              {plan.lost_refinements.length > 0 && (
                <div>
                  <div className="flex items-center space-x-1 mb-1">
                    <AlertTriangle className="w-3 h-3 text-orange-600" />
                    <span className="text-xs font-medium text-orange-800">Will be lost</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {plan.lost_refinements.map(stage => STAGE_LABELS[stage as StageType]).join(', ')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between p-4">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            
            <button
              onClick={handleExecute}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Play className="w-4 h-4" />
              <span>Execute Cascade</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CascadePlanner;